import { Component, ElementRef, Input, OnDestroy, OnInit, SimpleChanges, ViewChild } from '@angular/core';
import { NgbAccordionItem } from '@ng-bootstrap/ng-bootstrap';
import { Subscription } from 'rxjs';
import { take } from 'rxjs/operators';
import { Brand } from 'src/brands/brand';
import { HistoryService } from 'src/app/services/history.service';
import { Historyitem } from '../../../models/historyitem';
import { UitextService } from '../../../services/uitext.service';
import { ResultViewModel } from '../../../models/result-view-model';
import { CmsContentService } from '../../../services/cms-content.service';

@Component({
  selector: 'app-history',
  templateUrl: './history.component.html',
  styleUrls: ['./history.component.scss']
})
export class HistoryComponent implements OnInit, OnDestroy {
  @Input() result?: ResultViewModel;
  @ViewChild(NgbAccordionItem) accordionItem?: NgbAccordionItem;

  readonly selfAssessmentFirst = Brand.selfAssessmentFirst;

  analysis: Historyitem[] = [];
  gamtest: Historyitem[] = [];
  gamtest_days: number = 10;
  analysis_days: number = 10;

  private openSubscription?: Subscription;

  constructor(public historyService: HistoryService, public uitext: UitextService, private cmsContent: CmsContentService, private element: ElementRef<HTMLElement>) { }

  // The "How we calculated?" link in the result card (IBrand.resultLayout 'combined') opens the history
  ngOnInit(): void {
    this.openSubscription = this.historyService.openRequested$.subscribe(() => this.open());
  }

  ngOnDestroy(): void {
    this.openSubscription?.unsubscribe();
  }

  private open(): void {
    const item = this.accordionItem;
    if (!item)
      return;

    // Scroll once the content is expanded, so the page scrolls to where the history ends up
    if (item.collapsed) {
      item.shown.pipe(take(1)).subscribe(() => this.scrollIntoView());
      item.expand();
    } else {
      this.scrollIntoView();
    }

    // Keyboard and screen reader users continue from the accordion header, not from the link
    this.element.nativeElement.querySelector<HTMLElement>('.accordion-button')?.focus({ preventScroll: true });
  }

  private scrollIntoView(): void {
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.element.nativeElement.scrollIntoView({ behavior: reducedMotion ? 'auto' : 'smooth', block: 'start' });
  }

  async ngOnChanges(changes: SimpleChanges) {

    for (const propName in changes) {
      if (propName === "result") {

        let newResult = changes[propName].currentValue;

        if (newResult && newResult.analysis?.total?.group !== -1 && newResult.analysis.parts.length != 0) {
          // Built locally and assigned when done, so a result that's loaded again replaces the items instead of adding them twice
          const analysis: Historyitem[] = [];

          let category: string = "analysis";

          let moneyHeader = await this.uitext.getHeader(category, "money");
          let moneyimage = this.cmsContent.getCategoryImageUrl(this.getCategory(category), this.getSubCategory("money"), this.getImage(0));
          let moneytext = await this.uitext.getResult(category, this.getSubCategory("money"), 0);
          let moneypopover = await this.uitext.getPopover(category, this.getSubCategory("money"));
          let money = new Historyitem(moneyHeader, moneyimage, moneytext, moneypopover);
          analysis.push(money);

          let timeHeader = await this.uitext.getHeader(category, "time");
          let timeimage = this.cmsContent.getCategoryImageUrl(this.getCategory(category), this.getSubCategory("time"), this.getImage(0));
          let timetext = await this.uitext.getResult(category, this.getSubCategory("time"), 0);
          let timepopover = await this.uitext.getPopover(category, this.getSubCategory("time"));
          let time = new Historyitem(timeHeader, timeimage, timetext, timepopover);          
          analysis.push(time);

          let createdAtDate = new Date(newResult.analysis.created_at);
          let currentDate = new Date();
          let timeDifference = currentDate.getTime() - createdAtDate.getTime();
          this.analysis_days = Math.floor(timeDifference / (1000 * 60 * 60 * 24));

          let money_max = 0;
          let time_max = 0;

          for (var i in newResult.analysis.parts) {

            let part = newResult.analysis.parts[i];

            let header = await this.uitext.getHeader(category, part.category);
            let image = this.cmsContent.getCategoryImageUrl(this.getCategory(category), this.getSubCategory(part.category), this.getImage(part.group));
            let newItems = new Historyitem(header, image);

            if (part.category.startsWith("money")) {
              money.subitems.push(newItems);

              if (part?.group && part.group > money_max)
                money_max = part.group;
              money.image = this.cmsContent.getCategoryImageUrl(this.getCategory(category), this.getSubCategory("money"), this.getImage(money_max));
              money.text = await this.uitext.getResult(category, this.getSubCategory("money"), money_max);
            }
            else if (part.category.startsWith("time")) {
              time.subitems.push(newItems);

              if (part?.group && part?.group > time_max)
                time_max = part.group;
              time.image = this.cmsContent.getCategoryImageUrl(this.getCategory(category), this.getSubCategory("time"), this.getImage(time_max));
              time.text = await this.uitext.getResult(category, this.getSubCategory("time"), time_max);
            }
            else {
              let text = await this.uitext.getResult(category, this.getSubCategory(part.category), part?.group);
              newItems.text = text
              let popover = await this.uitext.getPopover(category, this.getSubCategory(part.category));
              newItems.popover = popover
              analysis.push(newItems);
            }
          }

          this.analysis = analysis;
        }

        if (newResult && newResult.gamtest) {
          let category: string = "gamtest";

          let createdAtDate = new Date(newResult.gamtest.created_at);
          let currentDate = new Date();
          let timeDifference = currentDate.getTime() - createdAtDate.getTime();
          this.gamtest_days = Math.floor(timeDifference / (1000 * 60 * 60 * 24));

          const gamtest: Historyitem[] = [];

          for (var i in newResult.gamtest.dimentions) {

            let gamtestDimention = newResult.gamtest.dimentions[i];

            let header = await this.uitext.getHeader(category, gamtestDimention.category);
            let image = this.cmsContent.getCategoryImageUrl(this.getCategory(category), this.getSubCategory(gamtestDimention.category), this.getImage(gamtestDimention.group));
            let text = await this.uitext.getResult(category, this.getSubCategory(gamtestDimention.category), gamtestDimention?.group);
            let popover = await this.uitext.getPopover(category, this.getSubCategory(gamtestDimention.category));
            let gamtestitem = new Historyitem(header, image, text, popover);
            gamtest.push(gamtestitem);
          }

          this.gamtest = gamtest;
        }
      }
    }
  }

  getCategory(category: string): string {
    if (category == "gamtest")
      return "self-assessment";
    return "gambling-data";
  }

  getSubCategory(subcategory: string): string {
    let categories = subcategory.split('_');
    return categories[categories.length - 1];
  }

  getImage(group: number | undefined): string {
    let image = "no-data.svg";
    switch (group) {
      case 1:
        image = "green.svg";
        break;
      case 2:
        image = "yellow.svg";
        break;
      case 3:
        image = "red.svg";
        break;
      default:
        break;
    }
    return image;
  }
}
